import { isRecord } from './parsingHelpers';

export interface ThreadToolResult {
    toolCallId: string | null;
    toolName: string;
    result: unknown;
}

const MCP_JSON_START = /[[{]/u;

/** Live MCP wraps JSON in text content; fixtures return the payload directly. */
export const unwrapMcpToolPayload = (value: unknown): unknown => {
    if (!isRecord(value) || !Array.isArray(value.content)) {
        return value;
    }

    const firstText = value.content.find(
        (item): item is { type?: unknown; text: string } =>
            isRecord(item) && typeof item.text === 'string',
    );
    if (firstText == null) {
        return value;
    }

    const jsonStart = firstText.text.search(MCP_JSON_START);
    if (jsonStart < 0) {
        return value;
    }

    try {
        return JSON.parse(firstText.text.slice(jsonStart));
    } catch {
        return value;
    }
};

/** MCP tools are exposed with a server prefix (e.g. `ff_overviewIncomeSplit_get`). */
export const isToolNameMatch = (toolName: string, baseName: string): boolean =>
    toolName === baseName || toolName.endsWith(`_${baseName}`);

const readToolName = (value: Record<string, unknown>): string | null => {
    if (typeof value.toolName === 'string') {
        return value.toolName;
    }
    return typeof value.name === 'string' ? value.name : null;
};

const readToolCallId = (value: Record<string, unknown>): string | null =>
    typeof value.toolCallId === 'string' ? value.toolCallId : null;

const getRawParts = (message: Record<string, unknown>): unknown[] => {
    if (Array.isArray(message.parts)) {
        return message.parts;
    }
    const { content } = message;
    if (isRecord(content) && Array.isArray(content.parts)) {
        return content.parts;
    }
    return Array.isArray(content) ? content : [];
};

const toToolResult = (
    part: Record<string, unknown>,
): ThreadToolResult | null => {
    const type = typeof part.type === 'string' ? part.type : '';

    if (type === 'tool-invocation' && isRecord(part.toolInvocation)) {
        const toolName = readToolName(part.toolInvocation);
        return toolName == null
            ? null
            : {
                  toolCallId: readToolCallId(part.toolInvocation),
                  toolName,
                  result: part.toolInvocation.result,
              };
    }

    if (
        type === 'tool-call' ||
        type === 'tool-result' ||
        type === 'dynamic-tool'
    ) {
        const toolName = readToolName(part);
        return toolName == null
            ? null
            : {
                  toolCallId: readToolCallId(part),
                  toolName,
                  result: part.result ?? part.output,
              };
    }

    if (type.startsWith('tool-')) {
        return {
            toolCallId: readToolCallId(part),
            toolName: type.slice('tool-'.length),
            result: part.output ?? part.result,
        };
    }

    return null;
};

/**
 * Completed tool results stored on a Mastra thread message, across the part
 * shapes Mastra has persisted over time. Calls without a result are skipped.
 */
export const extractToolResultsFromThreadMessage = (
    message: unknown,
): ThreadToolResult[] => {
    if (!isRecord(message)) {
        return [];
    }

    const candidates: ThreadToolResult[] = [];
    for (const part of getRawParts(message)) {
        const toolResult = isRecord(part) ? toToolResult(part) : null;
        if (toolResult != null) {
            candidates.push(toolResult);
        }
    }

    const { content } = message;
    if (isRecord(content) && Array.isArray(content.toolInvocations)) {
        for (const invocation of content.toolInvocations) {
            if (!isRecord(invocation)) {
                continue;
            }
            const toolName = readToolName(invocation);
            if (toolName != null) {
                candidates.push({
                    toolCallId: readToolCallId(invocation),
                    toolName,
                    result: invocation.result,
                });
            }
        }
    }

    const seenToolCallIds = new Set<string>();
    return candidates.filter((candidate) => {
        if (candidate.result == null) {
            return false;
        }
        if (candidate.toolCallId == null) {
            return true;
        }
        if (seenToolCallIds.has(candidate.toolCallId)) {
            return false;
        }
        seenToolCallIds.add(candidate.toolCallId);
        return true;
    });
};
